import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';
import BottomDrawer from '../../Drawer/Drawer';
import Button from '../../Button/Button';
import { SFH_ALLOWED_STATES } from '../../../constants/sfhAllowedStates';

// Collapsed view shows a 3x3 grid of chips before offering "Show More" (per Figma).
const COLLAPSED_STATES_LIMIT = 9;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 8px 8px 0;
  text-align: center;

  > svg {
    width: 36px;
    height: 36px;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  > svg {
    width: 20px;
    height: 20px;
    color: ${themeColors.colorTextPrimary};
  }
`;

const Title = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorTextPrimary};
`;

const Message = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.4;
  color: ${themeColors.colorTextSecondary};
`;

const StatesScrollArea = styled.div`
  width: 100%;
  height: 90px;
  overflow-y: auto;
`;

const StatesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
`;

const StateChip = styled.span`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  color: ${themeColors.colorLinkPrimary};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: 999px;
  padding: 10px 16px;
  white-space: nowrap;
`;

const ToggleLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  color: ${themeColors.colorLinkPrimary};
  text-decoration: underline;
`;

const SupportLink = styled.a`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 1;
  color: ${themeColors.colorLinkPrimary};
  text-decoration: underline;
`;

const DrawerFooter = styled.div`
  > button {
    width: 100%;
    font-family: ${variables.family.goldSilverHeaders}, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 1.3;
  }

  && > button,
  && > button:hover,
  && > button:focus,
  && > button:active {
    color: ${themeColors.colorSfhActionBlue};
    border-color: ${themeColors.colorSfhActionBlue};
  }
`;

const SellFromHomeStateLimitationDrawer = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const phone = t('sellWizard.sfhWizard.phone');

  // Always re-open collapsed, regardless of how it was left last time.
  useEffect(() => {
    if (isOpen) setExpanded(false);
  }, [isOpen]);

  const hasMoreStates = SFH_ALLOWED_STATES.length > COLLAPSED_STATES_LIMIT;
  const visibleStates = useMemo(
    () =>
      expanded
        ? SFH_ALLOWED_STATES
        : SFH_ALLOWED_STATES.slice(0, COLLAPSED_STATES_LIMIT),
    [expanded],
  );

  const footer = (
    <DrawerFooter>
      <Button
        variant="primaryOutlined"
        textTransform="none"
        onClick={onClose}
        data-cy="button-sfh-state-limitation-understood"
      >
        {t('sellWizard.sfhWizard.shipping.stateLimitationDrawer.cta')}
      </Button>
    </DrawerFooter>
  );

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      hideHeader
      maxHeight="80%"
      footer={footer}
    >
      <Content>
        <CloseButton
          type="button"
          onClick={onClose}
          data-cy="button-sfh-state-limitation-close"
        >
          <Close />
        </CloseButton>
        <InfoIcon />
        <Title>
          {t('sellWizard.sfhWizard.shipping.stateLimitationDrawer.title')}
        </Title>
        <Message>
          {t('sellWizard.sfhWizard.shipping.stateLimitationDrawer.subtitle')}
        </Message>
        <StatesScrollArea>
          <StatesGrid data-cy="grid-sfh-state-limitation-states">
            {visibleStates.map((state) => (
              <StateChip key={state.code}>{state.name}</StateChip>
            ))}
          </StatesGrid>
        </StatesScrollArea>
        {hasMoreStates && (
          <ToggleLink
            type="button"
            onClick={() => setExpanded((prev) => !prev)}
            data-cy="button-sfh-state-limitation-toggle"
          >
            {expanded
              ? t(
                  'sellWizard.sfhWizard.shipping.stateLimitationDrawer.showLess',
                )
              : t(
                  'sellWizard.sfhWizard.shipping.stateLimitationDrawer.showMore',
                )}
          </ToggleLink>
        )}
        <Message>
          {t(
            'sellWizard.sfhWizard.shipping.stateLimitationDrawer.expandingSoon',
          )}
        </Message>
        <SupportLink href={`tel:${phone.replace(/[^\d+]/g, '')}`}>
          {t('sellWizard.sfhWizard.needHelp')} {phone}
        </SupportLink>
      </Content>
    </BottomDrawer>
  );
};

SellFromHomeStateLimitationDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

export default SellFromHomeStateLimitationDrawer;
